/**
 * Apollo Error Message Extraction Utility
 * Extracts meaningful error messages from Apollo GraphQL errors
 */

export function getApolloErrorMessage(error: any): string {
  if (!error) return 'An unexpected error occurred. Please try again later.';

  // Priority: graphQLErrors -> networkError.result.errors -> error.message
  const gqlError = error?.graphQLErrors?.[0];
  if (gqlError) {
    // Prefer server-sent exception message if present
    if (gqlError.extensions?.exception?.message) {
      return String(gqlError.extensions.exception.message);
    }
    // Use the main GraphQL error message
    if (gqlError.message && gqlError.message !== 'An unexpected error occurred. Please try again later.') {
      return gqlError.message;
    }
  }

  // Sometimes networkError contains the body
  const netErrMsg = error?.networkError?.result?.errors?.[0]?.message;
  if (netErrMsg) return netErrMsg;

  if (error?.message) return error.message;
  return 'An unexpected error occurred. Please try again later.';
}

export function getApolloErrorRequestId(error: any): string | undefined {
  // Try to extract requestId from various places in the error
  const gqlError = error?.graphQLErrors?.[0];
  if (gqlError?.extensions?.requestId) {
    return gqlError.extensions.requestId;
  }
  
  const netError = error?.networkError?.result?.errors?.[0];
  if (netError?.extensions?.requestId) {
    return netError.extensions.requestId;
  }
  
  return undefined;
}

export function formatErrorForUser(error: any): string {
  const message = getApolloErrorMessage(error);
  const requestId = getApolloErrorRequestId(error);
  
  if (requestId) {
    return `${message} (ref: ${requestId})`;
  }
  
  return message;
}

// The Worker masks unexpected server errors with this text; it tells the
// customer nothing, so callers show their own fallback instead.
const MASKED_ERROR_MESSAGES = /^(unexpected error\.?|an unexpected error occurred\. please try again later\.)$/i;

/**
 * The backend's own message for a rejected mutation (for example "Cannot
 * delete video: It is referenced by ... Remove the video from these first."),
 * or `fallback` for network failures, masked server errors and plain Errors
 * that carry no GraphQL error.
 */
export function getBackendErrorMessage(error: any, fallback: string): string {
  if (!error || error?.networkError) return fallback;
  const message = error?.graphQLErrors?.[0]?.message;
  if (typeof message !== 'string') return fallback;
  const trimmed = message.trim();
  if (!trimmed || MASKED_ERROR_MESSAGES.test(trimmed)) return fallback;
  return trimmed;
}